
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Sparkles, Plus, FileText, TrendingUp, Zap, 
  CheckCircle2, Shield, Clock, Globe, Activity, 
  Loader2, Package, Users, Route, AlertTriangle 
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Cell 
} from 'recharts';
import { ImportModal } from '../components/ImportModal';
import { apiService } from '../services/apiService';
import { Project, Country, LoginLog, Order, User, Task, ProjectStatus, OrderStatus } from '../types';

interface DashboardModuleProps {
  activeCountry: Country;
}

const DashboardModule: React.FC<DashboardModuleProps> = ({ activeCountry }) => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [recentLogs, setRecentLogs] = useState<LoginLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Data State
  const [projects, setProjects] = useState<Project[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const [p, o, t, u, l] = await Promise.all([
          apiService.getProjects(),
          apiService.getOrders(),
          apiService.getTasks(),
          apiService.getUsers(),
          apiService.getLoginLogs()
        ]);
        setProjects(p);
        setOrders(o);
        setTasks(t);
        setUsers(u);
        setRecentLogs(l.slice(0, 5));
      } catch (e) {
        console.error("Dashboard Data Sync Error", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllData();
  }, [activeCountry]);

  // Calculated Stats for Active Branch
  const branchStats = useMemo(() => {
    const branchProjects = projects.filter(p => p.country === activeCountry);
    const activeProjects = branchProjects.filter(p => p.status !== ProjectStatus.COMPLETED);
    
    const branchOrders = orders.filter(o => {
        if (activeCountry === Country.TANZANIA) return o.id.startsWith('ST') || o.portOfDestination.includes('DAR');
        if (activeCountry === Country.UGANDA) return o.id.startsWith('SI') || o.portOfDestination.includes('KAMPALA');
        if (activeCountry === Country.KENYA) return o.id.startsWith('KI') || o.portOfDestination.includes('MOMBASA');
        return false;
    });
    const activeOrders = branchOrders.filter(o => o.currentStatus !== OrderStatus.CLEARED);
    
    const branchUsers = users.filter(u => u.homeCountry === activeCountry || u.allowedCountries.includes(activeCountry));
    const pendingTasks = tasks.filter(t => t.country === activeCountry && t.status !== 'Completed');

    return {
      activeProjectsCount: activeProjects.length,
      activeOrdersCount: activeOrders.length,
      personnelCount: branchUsers.length,
      pendingTasksCount: pendingTasks.length,
      projectData: branchProjects.map(p => ({
        name: p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name,
        progress: p.progress,
        fullLabel: p.name
      })).slice(0, 8)
    };
  }, [projects, orders, tasks, users, activeCountry]);

  const handleImportProject = async (project: Project) => {
    const success = await apiService.saveProject(project);
    if (success) {
      setImportSuccess(project.name);
      setProjects(prev => [project, ...prev]);
      setTimeout(() => setImportSuccess(null), 5000);
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-20 gap-4">
        <Loader2 className="animate-spin text-indigo-500" size={40} />
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400">Aggregating Intelligence...</p>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 animate-in fade-in duration-500">
      {importSuccess && (
        <div className="fixed top-20 right-8 z-[200] bg-emerald-600 text-white px-6 py-4 rounded-[24px] shadow-2xl flex items-center gap-4 animate-in slide-in-from-right duration-500">
            <CheckCircle2 size={24} />
            <div>
                <p className="text-xs font-black uppercase tracking-widest">Import Success</p>
                <p className="text-sm font-bold">{importSuccess} added to registry.</p>
            </div>
        </div>
      )}

      <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-800 tracking-tight uppercase">Command Center</h2>
          <p className="text-slate-500 font-medium">Branch Operations Hub: {activeCountry} Sector.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setIsImportModalOpen(true)}
            className="bg-indigo-600 text-white px-6 py-2.5 rounded-2xl font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all flex items-center gap-2 group"
          >
            <Sparkles size={18} className="group-hover:rotate-12 transition-transform" /> AI Quick Import
          </button>
        </div>
      </header>

      {/* Actual Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: 'Active Projects', value: branchStats.activeProjectsCount, sub: 'Branch Pipeline', icon: Route, color: 'bg-blue-500' },
          { title: 'Active Orders', value: branchStats.activeOrdersCount, sub: 'Supply Chain', icon: Package, color: 'bg-orange-500' },
          { title: 'Personnel', value: branchStats.personnelCount, sub: 'Authorized Node', icon: Users, color: 'bg-emerald-500' },
          { title: 'Pending Tasks', value: branchStats.pendingTasksCount, sub: 'Deliverables', icon: Activity, color: 'bg-indigo-500' },
        ].map((card, i) => (
          <div key={i} className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
            <div className="flex justify-between items-start mb-4">
              <div className={`w-12 h-12 ${card.color} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}>
                <card.icon size={22} />
              </div>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{card.sub}</span>
            </div>
            <h3 className="text-3xl font-black text-slate-800">{card.value}</h3>
            <p className="text-sm font-bold text-slate-500 mt-1">{card.title}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Real Data Visualization */}
          <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
            <div className="flex justify-between items-center mb-10">
               <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <TrendingUp className="text-indigo-500" size={20} />
                Project Progress Ledger
              </h3>
              <div className="bg-slate-50 border border-slate-100 px-3 py-1 rounded-xl">
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Scale</span>
              </div>
            </div>
            
            <div className="h-80 w-full">
              {branchStats.projectData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={branchStats.projectData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{fontSize: 10, fontWeight: 'bold', fill: '#94a3b8'}}
                    />
                    <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{fontSize: 10, fontWeight: 'bold', fill: '#94a3b8'}}
                        domain={[0, 100]}
                    />
                    <Tooltip 
                        contentStyle={{borderRadius: '16px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', padding: '12px'}}
                        cursor={{fill: '#f8fafc'}}
                    />
                    <Bar dataKey="progress" radius={[8, 8, 0, 0]} barSize={40}>
                      {branchStats.projectData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.progress === 100 ? '#10b981' : '#6366f1'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-300 gap-4 border-2 border-dashed border-slate-100 rounded-3xl">
                   <Route size={48} className="opacity-10" />
                   <p className="text-xs font-bold uppercase tracking-widest">No branch projects to visualize</p>
                </div>
              )}
            </div>
          </div>

          {/* System Access Audit Widget */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
               <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-100 text-indigo-600">
                    <Shield size={18} />
                  </div>
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest">System Access Audit</h3>
               </div>
               <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100 uppercase tracking-tighter">Live Monitor</span>
            </div>
            <div className="p-0">
               {recentLogs.length > 0 ? (
                 <div className="divide-y divide-slate-50">
                    {recentLogs.map((log) => (
                      <div key={log.id} className="p-5 flex items-center justify-between hover:bg-slate-50/80 transition-colors group">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold group-hover:scale-110 transition-transform">
                             {log.fullName.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-900">{log.fullName}</p>
                            <p className="text-[10px] text-slate-400 font-medium">@{log.username}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-8">
                           <div className="hidden sm:flex flex-col items-end">
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                <Globe size={10} className="text-indigo-400" /> {log.ipAddress}
                              </p>
                              <p className="text-[10px] text-slate-300 font-bold mt-1">Topology Location</p>
                           </div>
                           <div className="text-right min-w-[80px]">
                              <p className="text-xs font-black text-slate-700 flex items-center justify-end gap-1.5">
                                <Clock size={12} className="text-slate-300" />
                                {formatRelativeTime(log.loginTime)}
                              </p>
                              <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">Access Event</p>
                           </div>
                        </div>
                      </div>
                    ))}
                 </div>
               ) : (
                 <div className="p-12 text-center text-slate-400">
                    <Activity size={32} className="mx-auto mb-3 opacity-20" />
                    <p className="text-xs font-bold uppercase tracking-widest">No access logs detected.</p>
                 </div>
               )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 rounded-[2.5rem] p-8 shadow-xl text-white relative overflow-hidden">
            <div className="relative z-10">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                <Sparkles className="text-indigo-400" size={24} />
                Nexus Intelligence
              </h3>
              <div className="space-y-4">
                <div className="bg-white/5 backdrop-blur-md p-5 rounded-3xl border border-white/10 hover:bg-white/10 transition-all cursor-pointer">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap size={14} className="text-amber-400" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Pipeline Pulse</span>
                  </div>
                  <p className="text-sm text-slate-200 leading-relaxed italic font-medium">
                    {branchStats.activeProjectsCount > 0 
                      ? `"Operating at peak branch capacity with ${branchStats.activeProjectsCount} synchronized projects."` 
                      : `"System standby. Initialize project registry to begin monitoring pipeline."`}
                  </p>
                </div>
                
                {branchStats.pendingTasksCount > 0 && (
                  <div className="bg-indigo-600/20 p-5 rounded-3xl border border-indigo-500/30 hover:bg-indigo-600/30 transition-all cursor-pointer">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle size={14} className="text-indigo-400" />
                      <span className="text-[10px] font-black uppercase tracking-wider text-indigo-300">Action Required</span>
                    </div>
                    <p className="text-sm text-indigo-100 leading-relaxed font-bold">
                      {branchStats.pendingTasksCount} assignments are currently in standard pendency. Review allocation.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-3xl rounded-full -mr-16 -mt-16"></div>
          </div>

          <div className="bg-indigo-600 rounded-[2.5rem] p-8 text-white shadow-xl shadow-indigo-100">
             <h4 className="text-xs font-black mb-6 uppercase tracking-[0.2em] opacity-80">Operational Shortcuts</h4>
             <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setIsImportModalOpen(true)} className="bg-white/10 hover:bg-white/20 p-5 rounded-3xl flex flex-col items-center gap-3 transition-all border border-white/10">
                  <FileText size={24} />
                  <span className="text-[10px] font-black uppercase tracking-widest">AI Import</span>
                </button>
                <button className="bg-white/10 hover:bg-white/20 p-5 rounded-3xl flex flex-col items-center gap-3 transition-all border border-white/10">
                  <Plus size={24} />
                  <span className="text-[10px] font-black uppercase tracking-widest">New Quote</span>
                </button>
             </div>
          </div>
        </div>
      </div>

      <ImportModal 
        isOpen={isImportModalOpen} 
        onClose={() => setIsImportModalOpen(false)} 
        onImport={handleImportProject} 
        activeCountry={activeCountry}
      />
    </div>
  );
};

export default DashboardModule;
